// PAGE-CHOICE clarification — "did you mean HERE or the other page?". A filter query that NAMES another
// page's data is valid multiple ways: filter the current page, or OPEN that page filtered. Rather than guess,
// Otto asks; the pick IS the confirmation (each choice runs the matching flow — no second confirm).
// Deterministic frontend code, exactly like crossCapabilityClarify and the routing demotion — no prompt,
// no eval churn.
//
// Scope (user decisions 2026-07-09 / extended 2026-07-20): ONLY from the DASHBOARD, toward a reachable TABLE
// page the query NAMES (a bare "show me critical" filters the dashboard directly). It never fires on a
// refinement / removal / clear_all (you're mid-flow on the current page). Two candidate pages today:
//   - Recommendations (2-way): filter the dashboard, or open Recommendations filtered (its LOCAL filters).
//   - Buildings (3-way): because dashboard filters PROPAGATE to the project sub-pages, "filter the dashboard
//     then open Buildings" is a distinct, meaningful path (the dashboard-level filter carries over) vs.
//     filtering only the Buildings table locally. So Buildings offers: filter the dashboard (stay) /
//     filter the dashboard then open Buildings (propagation) / filter the Buildings table only (local).
import { normalizeText } from "../dashboardNavigation/navCatalog";
import { pageActionLabel, shortPageLabel, pagesNamedIn } from "../planner/pageManifest";

// Pages a dashboard filter query can be redirected to (checked in order; first NAMED + reachable wins).
const CANDIDATE_PAGES = ["recommendations", "buildings"];

// RESIDUAL-ACTION guard: a fused-goal request ("a report of my florida recommendations" = filter + export)
// should route `multi_task`, but when the classifier misses it arrives here as a filter_query — and the
// action half used to be silently dropped by the bare filter choice. These words, as whole words, mark that
// an EXPORT is part of the goal; the open-* choices then append it as a plan step so answering the page
// question can't lose the report. Defense-in-depth behind the classifier's own fused-goal tell.
export const EXPORT_WORDS = ["export", "download", "report", "reports", "excel", "spreadsheet", "xls", "xlsx"];
// Exported: the DATA-redirect seam (capabilityRedirect.buildDataRedirectProposal) splits a cross-page
// export request on the same rule, so both seams agree on what counts as "an export is part of the goal".
export const detectResidualAction = utterance => {
    const norm = ` ${normalizeText(utterance)} `;
    return EXPORT_WORDS.some(w => norm.includes(` ${w} `)) ? "export" : null;
};

// The choice buttons offered per page. `kind` drives execution in FilterAssistant.onPageChoice:
//   here          -> re-run the filter on the DASHBOARD (skip this prompt).
//   open-local    -> navigate to the page, then filter it LOCALLY (page's own filters / table column filter).
//   open-dashboard-> filter the DASHBOARD (persists + propagates), THEN navigate to the page (it inherits it).
// With a residual `action`, the open-* labels say the action runs there too (onPageChoice appends its step).
const choicesFor = (pageKey, target, action) => {
    // Refer to the destination the way Otto names it everywhere: a data-grid page is "Buildings Table View",
    // not "the Buildings page" (shortPageLabel for the bare name, pageActionLabel for the "Open …" phrasing).
    const place = shortPageLabel(target);
    const andAction = action === "export" ? " & export" : "";
    if (pageKey === "buildings") {
        return [
            { kind: "here", label: "Filter the dashboard" },
            { kind: "open-dashboard", label: `Filter the dashboard, then open ${place}${andAction}` },
            { kind: "open-local", label: `Open ${place} filtered${andAction} (this page only)` }
        ];
    }
    // Recommendations (and any future page) keep the original 2-way: filter here, or open there filtered locally.
    return [
        { kind: "here", label: "Filter the dashboard" },
        { kind: "open-local", label: `${pageActionLabel("Open", target)} filtered${andAction}` }
    ];
};

// Returns { target, choices, action } when the page choice applies, else null.
export const detectPageChoice = (utterance, intent, { page, navTargets } = {}) => {
    if (page !== "dashboard") return null; // direction: dashboard -> a table page only
    if (!intent || intent.intent_kind !== "filter_query") return null;
    if (intent.is_refinement || intent.clear_all) return null; // a refinement / clear stays on this page
    // Which pages did the utterance NAME? (Reachable ones only — a page this user can't open is not a choice.)
    // pagesNamedIn is the shared naming rule, so this prompt and the capability-redirect seam agree on what
    // counts as the user having already said WHERE.
    const named = pagesNamedIn(utterance, navTargets);
    for (const pageKey of CANDIDATE_PAGES) {
        const target = named.find(t => t.key === pageKey);
        if (target) {
            const action = detectResidualAction(utterance);
            return { target, choices: choicesFor(pageKey, target, action), action };
        }
    }
    return null;
};

// interpretResponse threads the request/session id (tracing + feedback); ctx = { utterance }.
export const buildPageChoiceProposal = (interpretResponse, ctx = {}, detection) => {
    const target = detection.target;
    return {
        pageChoice: true,
        needsClarification: true,
        target,
        choices: detection.choices || [],
        action: detection.action || null,
        utterance: ctx.utterance,
        requestId: interpretResponse && interpretResponse.request_id,
        sessionId: interpretResponse && interpretResponse.session_id,
        message:
            detection.action === "export"
                ? `Did you want to filter the dashboard, or ${pageActionLabel("open", target)} filtered — and export from there?`
                : `Did you want to filter the dashboard, or ${pageActionLabel("open", target)} filtered to that?`
    };
};
